import { useEffect, useState, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMap, Circle } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Link } from "react-router-dom";
import L from "leaflet";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t, pickLang } from "@/lib/i18n";
import { log } from "@/lib/logger";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ApplyDialog, CompanySheet } from "@/components/CompanyBits";
import { Building2, LocateFixed, X, Sparkles, Loader2, Star, List, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";

const CONTRACTS = ["stage", "emploi", "saisonnier", "alternance"];

// Module-level constants — kept out of the component so they don't produce
// a fresh reference on every render and don't churn React reconciliation.
const MAP_CONTAINER_STYLE = { height: "100%", width: "100%" };
const MAP_INITIAL_CENTER = [30, 10];
const CLUSTER_OPTS = {
  chunkedLoading: true,
  chunkInterval: 80,
  chunkDelay: 30,
  maxClusterRadius: 55,
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: false,
  disableClusteringAtZoom: 12,
};
const CENTER_MARKER_ICON = L.divIcon({
  className: "",
  html: '<div style="width:16px;height:16px;border-radius:9999px;background:#F97316;border:3px solid #fff;box-shadow:0 0 0 3px rgba(249,115,22,0.35)"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});
const CENTER_CIRCLE_PATH = { color: "#F97316", fillColor: "#F97316", fillOpacity: 0.08, weight: 1 };

// One glyph per canonical sector — picked to be readable at pin size (13-14px).
const SECTOR_EMOJI = {
  "Restauration": "🍽️",
  "Commerce": "🛒",
  "Beauté & Bien-être": "💇",
  "Santé": "🏥",
  "Services aux entreprises": "🧹",
  "Grandes entreprises": "🏢",
  "Finance & Assurance": "💰",
  "Immobilier": "🏠",
  "Automobile": "🚗",
  "Construction & Bâtiment": "🏗️",
  "Industrie & Fabrication": "🏭",
  "Mines & Ressources naturelles": "⛏️",
  "Énergie & Services publics": "⚡",
  "Technologie & Informatique": "💻",
  "Télécommunications & Médias": "📡",
  "Transport & Logistique": "🚚",
  "Agriculture & Agroalimentaire": "🌾",
  "Éducation": "🎓",
  "Arts, Culture & Médias": "🎨",
  "Sport & Loisirs": "⚽",
  "Hôtellerie & Tourisme": "🏨",
  "Sciences, Ingénierie & Services professionnels": "🔬",
  "Environnement & Gestion des déchets": "🌱",
  "Services sociaux & communautaires": "🤝",
  "Droit & Services juridiques": "⚖️",
  "Secteur public": "🏛️",
  "Autre": "📍",
};

const iconForSector = (sector) => SECTOR_EMOJI[sector] || "🏢";

const makeIcon = (company, isMatch, isBoosted, isSponsored) => {
  const classes = ["proxijob-marker"];
  if (isBoosted) classes.push("boosted");
  else if (isSponsored) classes.push("sponsored");
  else if (isMatch) classes.push("match");
  const glyph = iconForSector(company?.sector);
  return L.divIcon({
    className: "",
    html: `<div class="${classes.join(" ")}">${glyph}</div>`,
    iconSize: [40, 30],
    iconAnchor: [20, 15],
  });
};

const isCompanyBoosted = (c) => {
  if (!c?.boost_active) return false;
  if (!c.boost_until) return true;
  return new Date(c.boost_until).getTime() > Date.now();
};

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lng], 12, { duration: 1.2 }); }, [target, map]);
  return null;
}

function haversineKm(a, b, c, d) {
  const R = 6371;
  const dLat = (c - a) * Math.PI / 180;
  const dLng = (d - b) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a * Math.PI / 180) * Math.cos(c * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function MapExplorer({ preFilter = {} }) {
  const { lang, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [filters, setFilters] = useState({ sector: "", country: "", city: "", contract_type: "", hosts_interns: false, accepts_students: false, q: "", ...preFilter });
  const [center, setCenter] = useState(null); // { lat, lng, label }
  const [radiusKm, setRadiusKm] = useState(50);
  const [selected, setSelected] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [stats, setStats] = useState({ total_count: 0, filtered_count: 0 });
  const [spotlight, setSpotlight] = useState(null);
  const [spotlightDismissed, setSpotlightDismissed] = useState(false);
  // View mode toggle: on small screens the user picks List OR Map; on
  // large screens both are shown side-by-side ("split"). The initial value
  // is decided on first render and persists across resizes via user action.
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "split";
    return window.innerWidth >= 1024 ? "split" : "map";
  });
  const isAdmin = user?.user_type === "admin";

  const importPlacesHere = async () => {
    if (!isAdmin) return;
    if (!center) { toast.error(lang === "fr" ? "Cliquez d'abord sur « Me localiser » ou définissez un centre" : "Set a center first via « Locate me »"); return; }
    if (!window.confirm(lang === "fr"
      ? `Importer les PME réelles dans un rayon de ${Math.min(radiusKm, 20)} km autour de ${center.label} ? (Google Places)`
      : `Import real businesses within ${Math.min(radiusKm, 20)} km of ${center.label}? (Google Places)`)) return;
    setImporting(true);
    try {
      const { data } = await http.post("/companies/import-places", {
        lat: center.lat,
        lng: center.lng,
        radius_km: Math.min(radiusKm, 20),
        country: filters.country || undefined,
        city: filters.city || undefined,
        max_results: 20,
      });
      toast.success(lang === "fr"
        ? `${data.imported} PME importées (${data.skipped} ignorées)`
        : `${data.imported} businesses imported (${data.skipped} skipped)`);
      fetchCompanies();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString().slice(0, 160) || (lang === "fr" ? "Import échoué" : "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  const importBulkCity = async () => {
    if (!isAdmin || !center) return;
    const gs = window.prompt(lang === "fr" ? "Taille de grille (2 à 5, ex : 3 = 9 points)" : "Grid size (2-5, e.g. 3 = 9 points)", "3");
    if (!gs) return;
    const grid = Math.max(2, Math.min(5, parseInt(gs, 10) || 3));
    if (!window.confirm(lang === "fr"
      ? `Scanner ${grid}×${grid} = ${grid*grid} points autour de ${center.label} ? Cela consommera ~${grid*grid} appels Places.`
      : `Scan ${grid}×${grid} = ${grid*grid} points around ${center.label}? Uses ~${grid*grid} Places calls.`)) return;
    setImporting(true);
    try {
      const { data } = await http.post("/companies/import-places-bulk", {
        center_lat: center.lat,
        center_lng: center.lng,
        country: filters.country || undefined,
        city: filters.city || undefined,
        grid_size: grid,
        cell_km: 2,
      });
      toast.success(lang === "fr"
        ? `Scan terminé : ${data.imported} entreprises · ${data.points_scanned} points`
        : `Scan done: ${data.imported} businesses · ${data.points_scanned} points`);
      fetchCompanies();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString().slice(0, 160) || (lang === "fr" ? "Scan échoué" : "Scan failed"));
    } finally {
      setImporting(false);
    }
  };

  const fetchCompanies = useCallback(async () => {
    const params = {};
    if (filters.sector) params.sector = filters.sector;
    if (filters.country) params.country = filters.country;
    if (filters.city) params.city = filters.city;
    if (filters.hosts_interns) params.hosts_interns = true;
    if (filters.accepts_students) params.accepts_students = true;
    if (filters.contract_type) params.contract_type = filters.contract_type;
    if (filters.q) params.q = filters.q;
    const [listRes, statsRes] = await Promise.all([
      http.get("/companies", { params }),
      http.get("/companies/stats", { params }).catch(() => ({ data: null })),
    ]);
    setCompanies(listRes.data);
    if (statsRes.data) setStats(statsRes.data);
  }, [filters]);

  useEffect(() => {
    http.get("/companies/sectors").then((r) => setSectors((r.data || []).filter(Boolean)));
    http.get("/companies/countries").then((r) => setCountries((r.data || []).filter(Boolean)));
    http.get("/companies/spotlight").then((r) => {
      if (r.data && r.data.id) {
        setSpotlight(r.data);
        // Persist dismissal per week so the user only sees a given banner once.
        const dismissedWeek = sessionStorage.getItem("proxijob_spotlight_dismissed");
        if (dismissedWeek && dismissedWeek === r.data._spotlight_week) setSpotlightDismissed(true);
      }
    }).catch(() => {});
    // Bootstraps once on mount — sectors/countries/spotlight are static-ish
    // per session, so we don't need to re-fetch on state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh the city dropdown whenever the country filter changes — always
  // hits the server so we cover every referenced city, not only the ones
  // in the current paginated result.
  useEffect(() => {
    const params = filters.country ? { country: filters.country } : {};
    http.get("/companies/cities", { params })
      .then((r) => setAllCities(r.data || []))
      .catch(() => setAllCities([]));
  }, [filters.country]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  // Auto-detect location on first mount (browser geolocation → IP fallback).
  // Runs only once per session to avoid annoying prompts.
  useEffect(() => {
    if (center) return;
    if (sessionStorage.getItem("proxijob_auto_locate_done")) return;
    sessionStorage.setItem("proxijob_auto_locate_done", "1");

    const runIpFallback = async () => {
      try {
        const r = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        if (!r.ok) return;
        const d = await r.json();
        if (d.latitude && d.longitude) {
          setCenter({
            lat: parseFloat(d.latitude),
            lng: parseFloat(d.longitude),
            label: [d.city, d.country_name].filter(Boolean).join(", ") || (lang === "fr" ? "Ma région" : "My region"),
          });
        }
      } catch (e) {
        // IP geolocation is a best-effort fallback; log so we can spot outages in prod
        // without breaking the map when ipapi is unreachable.
        log.warn("[MapExplorer] IP geolocation failed:", e?.message || e);
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCenter({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: pickLang(lang, { fr: "Ma position", en: "My position", es: "Mi ubicación", de: "Mein Standort" }),
        }),
        () => runIpFallback(),
        { enableHighAccuracy: false, timeout: 4000, maximumAge: 300000 }
      );
    } else {
      runIpFallback();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cities = useMemo(() => {
    // Prefer the exhaustive server-side list (all 8 000+ cities across DB),
    // and merge in cities of the currently loaded results so the dropdown
    // never lags behind a fresh import.
    const merged = new Set(allCities);
    companies.forEach((c) => { if (c.city) merged.add(c.city); });
    return [...merged].sort((a, b) => a.localeCompare(b, lang || "fr"));
  }, [allCities, companies, lang]);

  // Apply radius filter client-side when a center is set; boosted > sponsored come first
  const visibleCompanies = useMemo(() => {
    const sortRank = (c) => (isCompanyBoosted(c) ? 0 : c.sponsored ? 1 : 2);
    const base = center
      ? companies
          .map((c) => ({ ...c, _dist: haversineKm(center.lat, center.lng, c.lat, c.lng) }))
          .filter((c) => c._dist <= radiusKm)
      : companies;
    return [...base].sort((a, b) => {
      const r = sortRank(a) - sortRank(b);
      if (r !== 0) return r;
      if (center) return (a._dist ?? 0) - (b._dist ?? 0);
      return (b.match_score || 0) - (a.match_score || 0);
    });
  }, [companies, center, radiusKm]);

  const locateMe = () => {
    if (!navigator.geolocation) { toast.warning(t(lang, "locate_denied"), { duration: 4000 }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: pickLang(lang, { fr: "Ma position", en: "My position" }) });
        toast.success(t(lang, "center_set"));
      },
      () => toast.warning(t(lang, "locate_denied"), { duration: 4000, description: pickLang(lang, {
        fr: "Nous utilisons votre région approximative à la place.",
        en: "We use your approximate region instead.",
        es: "Usamos tu región aproximada en su lugar.",
        de: "Wir verwenden Ihre ungefähre Region.",
      }) }),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <Layout>
      {/* Mobile-first view toggle — hidden on lg+ where both panels show. */}
      <div className="lg:hidden sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-3 py-2 flex items-center justify-center gap-1.5">
        <button
          data-testid="view-toggle-list"
          onClick={() => setViewMode("list")}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${viewMode === "list" ? "bg-[#1E3A8A] text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
        >
          <List className="w-3.5 h-3.5" /> {lang === "fr" ? "Liste" : "List"}
        </button>
        <button
          data-testid="view-toggle-map"
          onClick={() => setViewMode("map")}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${viewMode === "map" ? "bg-[#F97316] text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
        >
          <MapIcon className="w-3.5 h-3.5" /> {lang === "fr" ? "Carte" : "Map"}
        </button>
      </div>
      <div className={`grid lg:grid-cols-[380px_1fr] h-[calc(100vh-4rem-46px)] lg:h-[calc(100vh-4rem)]`}>
        {/* Sidebar */}
        <aside className={`${viewMode === "map" ? "hidden" : "block"} lg:block border-r border-slate-200 bg-white overflow-y-auto p-6 space-y-5`}>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "companies_found")}</p>
              <p data-testid="visible-count" className="font-display text-3xl font-bold text-[#1E3A8A]">
                {visibleCompanies.length.toLocaleString(lang)}
              </p>
              {stats.total_count > 0 && (
                <p data-testid="total-count" className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                  {pickLang(lang, {
                    fr: `sur ${stats.filtered_count.toLocaleString("fr")} filtrées · ${stats.total_count.toLocaleString("fr")} référencées`,
                    en: `of ${stats.filtered_count.toLocaleString("en")} filtered · ${stats.total_count.toLocaleString("en")} indexed`,
                    es: `de ${stats.filtered_count.toLocaleString("es")} filtradas · ${stats.total_count.toLocaleString("es")} registradas`,
                    de: `von ${stats.filtered_count.toLocaleString("de")} gefiltert · ${stats.total_count.toLocaleString("de")} erfasst`,
                  })}
                </p>
              )}
            </div>
            <Button data-testid="locate-me-btn" size="sm" variant="outline" onClick={locateMe} className="rounded-full text-xs">
              <LocateFixed className="w-3.5 h-3.5 mr-1" /> {t(lang, "locate_me")}
            </Button>
          </div>

          {isAdmin && (
            <div className="space-y-2">
              <Button
                data-testid="import-places-btn"
                onClick={importPlacesHere}
                disabled={importing || !center}
                className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full h-9 text-xs disabled:opacity-50"
              >
                {importing
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {lang === "fr" ? "Import en cours…" : "Importing…"}</>
                  : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> {lang === "fr" ? "Importer PME (Google Places)" : "Import businesses (Google Places)"}</>
                }
              </Button>
              <Button
                data-testid="import-places-bulk-btn"
                onClick={importBulkCity}
                disabled={importing || !center}
                variant="outline"
                className="w-full border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-full h-9 text-xs disabled:opacity-50"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {lang === "fr" ? "Scan quartier (grille)" : "Bulk scan (grid)"}
              </Button>
            </div>
          )}

          {center && (
            <div className="rounded-xl bg-[#FFF7ED] border border-[#F97316]/30 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#C2410C] font-semibold">{center.label}</span>
                <button onClick={() => setCenter(null)} data-testid="reset-center-btn" className="text-[#C2410C] hover:text-[#EA580C]"><X className="w-3.5 h-3.5" /></button>
              </div>
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs">{t(lang, "radius")}</Label>
                  <span className="text-xs font-semibold text-[#1E3A8A]">{radiusKm} {t(lang, "km")}</span>
                </div>
                <Slider data-testid="radius-slider" value={[radiusKm]} onValueChange={(v) => setRadiusKm(v[0])} min={5} max={500} step={5} />
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label>{t(lang, "filter_sector")}</Label>
              <Select value={filters.sector || "__all__"} onValueChange={(v) => setFilters({ ...filters, sector: v === "__all__" ? "" : v })}>
                <SelectTrigger data-testid="filter-sector" className="mt-1.5"><SelectValue placeholder={t(lang, "all_sectors")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t(lang, "all_sectors")}</SelectItem>
                  {sectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t(lang, "filter_country")}</Label>
              <Select value={filters.country || "__all__"} onValueChange={(v) => setFilters({ ...filters, country: v === "__all__" ? "" : v, city: "" })}>
                <SelectTrigger data-testid="filter-country" className="mt-1.5"><SelectValue placeholder={t(lang, "all_countries")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t(lang, "all_countries")}</SelectItem>
                  {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t(lang, "filter_city")}</Label>
              <Select value={filters.city || "__all__"} onValueChange={(v) => setFilters({ ...filters, city: v === "__all__" ? "" : v })}>
                <SelectTrigger data-testid="filter-city" className="mt-1.5"><SelectValue placeholder={t(lang, "all_cities")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t(lang, "all_cities")}</SelectItem>
                  {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t(lang, "filter_contract")}</Label>
              <Select value={filters.contract_type || "__all__"} onValueChange={(v) => setFilters({ ...filters, contract_type: v === "__all__" ? "" : v })}>
                <SelectTrigger data-testid="filter-contract" className="mt-1.5"><SelectValue placeholder={t(lang, "all_contracts")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t(lang, "all_contracts")}</SelectItem>
                  {CONTRACTS.map((c) => <SelectItem key={c} value={c}>{t(lang, `contract_${c}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between py-2">
              <Label className="cursor-pointer" htmlFor="hi">{t(lang, "filter_internship")}</Label>
              <Switch id="hi" data-testid="filter-interns" checked={filters.hosts_interns} onCheckedChange={(v) => setFilters({ ...filters, hosts_interns: v })} />
            </div>
            <div className="flex items-center justify-between py-2 rounded-lg bg-[#FFF7ED] px-2 border border-[#F97316]/30">
              <Label className="cursor-pointer text-[#C2410C] font-semibold" htmlFor="stu">{t(lang, "filter_students")}</Label>
              <Switch id="stu" data-testid="filter-students" checked={filters.accepts_students} onCheckedChange={(v) => setFilters({ ...filters, accepts_students: v })} />
            </div>
            <Input data-testid="filter-q" placeholder={lang === "fr" ? "Rechercher..." : "Search..."} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            <Button data-testid="filter-reset" variant="outline" size="sm" className="w-full rounded-full" onClick={() => setFilters({ sector: "", country: "", city: "", contract_type: "", hosts_interns: false, accepts_students: false, q: "" })}>
              {t(lang, "filter_reset")}
            </Button>
          </div>

          <div className="space-y-2">
            {visibleCompanies.map((c) => (
              <button
                key={c.id}
                data-testid={`co-list-${c.id}`}
                onClick={() => setSelected(c)}
                className={`w-full text-left rounded-xl p-3 border transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  selected?.id === c.id ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                      {isCompanyBoosted(c) && <span data-testid={`co-boosted-${c.id}`} title="Boosté" className="text-[#F97316] text-xs">★</span>}
                      {c.sponsored && !isCompanyBoosted(c) && <span data-testid={`co-sponsored-${c.id}`} title="Sponsor" className="text-[#1E3A8A] text-xs">◆</span>}
                      {c.name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{c.city}, {c.country} {c._dist !== undefined && `· ${c._dist.toFixed(0)} ${t(lang, "km")}`}</p>
                  </div>
                  {c.hosts_interns && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] font-semibold">Stage</span>}
                </div>
                <p className="text-xs text-slate-600 mt-1.5 line-clamp-2">{c.description}</p>
              </button>
            ))}
            {visibleCompanies.length === 0 && (
              <div className="text-center py-8 text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl">
                <Building2 className="w-6 h-6 mx-auto mb-2 opacity-40" />
                {t(lang, "empty_map")}
              </div>
            )}
          </div>
        </aside>

        {/* Map */}
        <div className={`${viewMode === "list" ? "hidden" : "block"} lg:block relative`}>
          {spotlight && !spotlightDismissed && (
            <div
              data-testid="spotlight-banner"
              className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] max-w-[92%] sm:max-w-md"
            >
              <div className="relative rounded-2xl bg-gradient-to-r from-[#1E3A8A] to-[#F97316] p-[1.5px] shadow-lg">
                <Link
                  to={`/vitrine/${spotlight.id}`}
                  data-testid="spotlight-link"
                  className="flex items-center gap-3 rounded-[15px] bg-white/95 backdrop-blur px-4 py-2.5 hover:bg-white transition-colors"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] text-white shadow-inner">
                    <Star className="h-4 w-4 fill-white" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#F97316]">
                      {pickLang(lang, {
                        fr: "Sponsor de la semaine",
                        en: "Sponsor of the week",
                        es: "Patrocinador de la semana",
                        de: "Sponsor der Woche",
                      })}
                    </p>
                    <p className="truncate text-sm font-semibold text-[#1E3A8A]">
                      {spotlight.name}
                      <span className="ml-1.5 text-xs font-normal text-slate-500">· {spotlight.city}</span>
                    </p>
                  </div>
                </Link>
                <button
                  data-testid="spotlight-close"
                  onClick={(e) => {
                    e.preventDefault();
                    sessionStorage.setItem("proxijob_spotlight_dismissed", spotlight._spotlight_week || "");
                    setSpotlightDismissed(true);
                  }}
                  aria-label="Dismiss"
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-slate-500 shadow-md hover:text-slate-900"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
          <MapContainer center={MAP_INITIAL_CENTER} zoom={3} style={MAP_CONTAINER_STYLE} scrollWheelZoom>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MarkerClusterGroup {...CLUSTER_OPTS}>
              {visibleCompanies.map((c) => (
                <Marker
                  key={c.id}
                  position={[c.lat, c.lng]}
                  icon={makeIcon(c, c.match_score >= 80, isCompanyBoosted(c), !!c.sponsored)}
                  eventHandlers={{ click: () => setSelected(c) }}
                />
              ))}
            </MarkerClusterGroup>
            {center && (
              <>
                <Marker position={[center.lat, center.lng]} icon={CENTER_MARKER_ICON} />
                <Circle center={[center.lat, center.lng]} radius={radiusKm * 1000} pathOptions={CENTER_CIRCLE_PATH} />
              </>
            )}
            <FlyTo target={selected || center} />
          </MapContainer>

          {selected && (
            <CompanySheet
              company={selected}
              onClose={() => setSelected(null)}
              onApply={() => setApplyOpen(true)}
            />
          )}
        </div>
      </div>

      <ApplyDialog open={applyOpen} onOpenChange={setApplyOpen} company={selected} />
    </Layout>
  );
}
